/**
 * Configuration Axios pour les appels API
 */

import axios from 'axios';

// Utiliser le proxy Vite en développement, ou l'URL absolue en staging/production.
// En dev, Vite proxy redirige /api vers localhost:3001.
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Timeout pour éviter que les requêtes pendent indéfiniment (surtout au premier lancement / cold start)
const REQUEST_TIMEOUT_MS = Number(import.meta.env.VITE_REQUEST_TIMEOUT_MS || 30000);

// Créer une instance axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token à chaque requête
api.interceptors.request.use(
  (config) => {
    // Try both 'token' and 'authToken' for backward compatibility
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // FormData: remove Content-Type so axios sets multipart/form-data with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs de réponse
function clearAuthAndRedirect() {
  localStorage.removeItem('token');
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
    window.location.href = '/';
  }
}

// Refresh the access token via a BARE axios call (not `api`) so it doesn't
// re-enter this interceptor (no recursion) and avoids a circular import.
async function performTokenRefresh() {
  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) throw new Error('No refresh token');
  const res = await axios.post(
    `${API_BASE_URL}/auth/refresh`,
    { refreshToken: storedRefreshToken },
    { timeout: REQUEST_TIMEOUT_MS },
  );
  const data = res.data;
  if (data?.success && data?.data?.token) {
    localStorage.setItem('token', data.data.token);
    localStorage.setItem('authToken', data.data.token);
    if (data.data.refreshToken) {
      localStorage.setItem('refreshToken', data.data.refreshToken);
    }
    return data.data.token;
  }
  throw new Error('Token refresh failed');
}

// Single-flight refresh: on a 401, refresh once and retry the original request;
// concurrent 401s queue behind the same refresh. Only log out if the refresh
// itself fails (or there's nothing to refresh). Network errors never log out —
// this is what keeps a session alive while offline and lets the outbox replay
// survive a token that expired during the offline window.
let isRefreshing = false;
let refreshWaiters = [];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // No response = unreachable/offline. Never log out; let the caller decide.
    if (!error.response) {
      return Promise.reject(error);
    }

    const original = error.config || {};
    const status = error.response.status;
    const url = original.url || '';
    const isAuthEndpoint = url.includes('/auth/refresh') || url.includes('/auth/login');

    if (status === 401 && !original._retry && !isAuthEndpoint) {
      if (!localStorage.getItem('refreshToken')) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }
      original._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshWaiters.push((token) => {
            if (!token) return reject(error);
            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${token}`;
            resolve(api(original));
          });
        });
      }

      isRefreshing = true;
      try {
        const newToken = await performTokenRefresh();
        isRefreshing = false;
        refreshWaiters.forEach((cb) => cb(newToken));
        refreshWaiters = [];
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        isRefreshing = false;
        refreshWaiters.forEach((cb) => cb(null));
        refreshWaiters = [];
        clearAuthAndRedirect();
        return Promise.reject(error);
      }
    }

    // A 401 we can't recover (already retried, or the auth endpoint itself).
    if (status === 401) {
      clearAuthAndRedirect();
    }
    return Promise.reject(error);
  }
);

export { API_BASE_URL };
export default api;
